import React, { useEffect, useState } from 'react';
import { Loader2, Download, AlertTriangle } from 'lucide-react';
import { getUnappliedPayments, downloadCsv, inr, inrFull } from '../data';
import type { UnappliedPayments as UnappliedT } from '../types';

/**
 * Unapplied-payments queue: money paid in Zoho but matched to no bill. A review list —
 * by-vendor on screen, full payment detail on download. Not all errors (advances/timing).
 */
export const UnappliedPayments: React.FC = () => {
	const [d, setD] = useState<UnappliedT | null>(null);
	const [err, setErr] = useState<string | null>(null);

	useEffect(() => {
		let on = true;
		getUnappliedPayments()
			.then(x => on && setD(x))
			.catch(e => on && setErr(e?.message || 'Failed'));
		return () => {
			on = false;
		};
	}, []);

	if (err) return <div className='p-6 text-red-600'>Error: {err}</div>;
	if (!d)
		return (
			<div className='flex items-center gap-2 p-6 text-gray-500'>
				<Loader2 className='animate-spin' size={18} /> Loading unapplied payments…
			</div>
		);

	const exportCsv = () => {
		downloadCsv(
			'unapplied-payments.csv',
			['Vendor', 'Payment #', 'Date', 'Amount (INR)', 'Paid through', 'Reference', 'Mode'],
			d.payments.map(p => [
				p.vendor,
				p.payment_number,
				String(p.payment_date).slice(0, 10),
				p.amount,
				p.account ?? '',
				p.reference ?? '',
				p.mode ?? '',
			])
		);
	};

	return (
		<div className='space-y-6'>
			<section className='bg-white rounded-lg border border-gray-200 p-5'>
				<div className='flex items-start justify-between gap-3 mb-1'>
					<div>
						<h3 className='font-semibold text-gray-900 flex items-center gap-2'>
							<AlertTriangle size={18} className='text-amber-600' />
							Unapplied payments — paid, matched to no bill in Zoho
						</h3>
						<p className='text-xs text-gray-400 mt-1 max-w-2xl'>{d.note}</p>
					</div>
					<button
						onClick={exportCsv}
						className='flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded hover:bg-gray-100 whitespace-nowrap'>
						<Download size={15} />
						Download CSV ({d.summary.payments})
					</button>
				</div>

				<div className='grid grid-cols-3 gap-3 my-4'>
					<Tile label='Unapplied value' value={inr(d.summary.value)} tone='amber' />
					<Tile label='Payments' value={String(d.summary.payments)} tone='gray' />
					<Tile label='Vendors' value={String(d.summary.vendors)} tone='gray' />
				</div>
			</section>

			<section className='bg-white rounded-lg border border-gray-200 p-5'>
				<h3 className='font-semibold text-gray-900 mb-3'>By vendor (largest first)</h3>
				<table className='w-full text-sm'>
					<thead className='text-gray-500 text-xs uppercase'>
						<tr>
							<th className='text-left py-2'>Vendor</th>
							<th className='text-right py-2'>Payments</th>
							<th className='text-right py-2'>Unapplied value</th>
						</tr>
					</thead>
					<tbody>
						{d.byVendor.map(v => (
							<tr key={v.vendor} className='border-t border-gray-100'>
								<td className='py-2 text-gray-800'>{v.vendor}</td>
								<td className='py-2 text-right text-gray-500'>{v.payments}</td>
								<td className='py-2 text-right font-medium text-amber-700'>{inrFull(v.value)}</td>
							</tr>
						))}
					</tbody>
				</table>
			</section>
		</div>
	);
};

const Tile: React.FC<{ label: string; value: string; tone: 'amber' | 'gray' }> = ({
	label,
	value,
	tone,
}) => (
	<div
		className={`p-3 rounded-lg border ${
			tone === 'amber' ? 'bg-amber-50 border-amber-100' : 'bg-gray-50 border-gray-100'
		}`}>
		<div className='text-xs text-gray-500'>{label}</div>
		<div className={`text-xl font-bold ${tone === 'amber' ? 'text-amber-700' : 'text-gray-900'}`}>
			{value}
		</div>
	</div>
);
